import { getErrorMessage } from "@/helpers/handleErrorResponse";
import {
  ForgotPasswordFormData,
  LoginPayload,
  LogoutPayload,
  LogoutResponse,
  PasswordFormPayload,
  SsoProviderQuery,
  SsoTokenExchangePayload,
  SsoTokenRequestPayload
} from "@/types/auth";
import { CustomError } from "@/utils/errors";

import type { AxiosError } from "axios";
import axios from "axios";

import axiosInstance from "../config";

/**
 * Legacy (external IdP) authorization-code exchange. Kept separate from
 * {@link requestSsoToken} so the LOKASI-only `withCredentials` does not leak
 * into the cross-site call to the external provider.
 */
export const ssoTokenRequest = async (
  url: string,
  body: SsoTokenRequestPayload
) => {
  try {
    // Use normal axios instead of axiosInstance to avoid interceptors
    const { data } = await axios.post(url, body, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      }
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const requestSsoToken = async (
  url: string,
  body: SsoTokenRequestPayload
) => {
  try {
    const { data } = await axios.post(url, body, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      withCredentials: true
    });
    return data;
  } catch (error) {
    // Keep IdP's OAuth error body (`{ error: "invalid_grant", error_description }`) on the CustomError
    const { status, message } = getErrorMessage(error);
    throw new CustomError(
      message,
      status,
      (error as AxiosError)?.response?.data ?? null
    );
  }
};

export const exchangeSsoToken = async ({
  idToken,
  tenantId
}: SsoTokenExchangePayload) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/token", {
      grant_type: "token_exchange",
      token_type: "access_token",
      client_id: import.meta.env.VITE_APP_CODE,
      app_id: import.meta.env.VITE_APP_CODE,
      token: idToken,
      tenant_id: tenantId
    });
    return data;
  } catch (error) {
    // Our own BFF, so the standard envelope handling applies: `getErrorMessage`
    // resolves `reason` to a display message, and the reason code rides along on
    // the CustomError for the callback to forward.
    const { status, message, reason } = getErrorMessage(error);
    throw new CustomError(message, status, undefined, reason);
  }
};

export const getSsoProvider = async (params: SsoProviderQuery) => {
  try {
    const { data } = await axiosInstance.get("/v1/auth/sso/providers", {
      params
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

const buildLoginPayload = (body: LoginPayload) => {
  const grantType = body.password ? "password" : "token_exchange";
  return {
    grant_type: grantType,
    client_id: import.meta.env.VITE_APP_CODE,
    ...(body.password
      ? {
          email_address: body.email,
          password: body.password
        }
      : {
          token: body.token,
          app_id: body.client_id,
          tenant_id: body.tenant_id,
          token_type: "access_token"
        })
  };
};

export const loginApi = async (body: LoginPayload) => {
  try {
    const { data } = await axiosInstance.post(
      "/v1/auth/token",
      buildLoginPayload(body)
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postActivateUser = async ({
  token,
  password
}: PasswordFormPayload) => {
  try {
    const { data } = await axiosInstance.post("/v1/activate-user", {
      token,
      password
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postResetPasword = async ({
  token,
  password
}: PasswordFormPayload) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/reset-password", {
      token,
      new_password: password
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postForgotPassword = async ({ email }: ForgotPasswordFormData) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/forgot-password", {
      email_address: email.toLowerCase()
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postVerifyToken = async (token: string) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/verify-token", {
      token
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

/**
 * `global` tells the BFF to end the SSO session at the IdP too, not just this
 * app's session. When it does, the response carries `idp_logout_url` — the
 * endpoint the browser must actually visit for the IdP to drop its cookie.
 */
export const postLogout = async ({
  isGlobal
}: LogoutPayload = {}): Promise<LogoutResponse> => {
  const urlParams = new URLSearchParams();

  if (isGlobal) urlParams.append("global", "true");
  const queryString = urlParams.toString() ? `?${urlParams.toString()}` : "";

  try {
    const { data } = await axiosInstance.post(`/v1/auth/logout${queryString}`);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const patchChangePassword = async ({
  currentPassword,
  newPassword
}: {
  currentPassword: string;
  newPassword: string;
}) => {
  try {
    const { data } = await axiosInstance.patch("/v1/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAuthStatus = async () => {
  try {
    const { data } = await axiosInstance.get("/v1/auth/status");
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};
